import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SignalRProvider } from '@/contexts/SignalRContext';
import { queryClient } from '@/lib/queryClient';
import { Layout } from '@/components/layout/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { UserProfilePage } from '@/pages/UserProfilePage';
import { EliminationsPage } from '@/pages/EliminationsPage';
import { DangerZonePage } from '@/pages/DangerZonePage';
import { GameweekPage } from '@/pages/GameweekPage';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { LeagueStandings } from '@/components/league/LeagueStandings';
import { SeasonManagementPage } from '@/pages/admin/SeasonManagementPage';
import { SeasonApprovalsPage } from '@/pages/admin/SeasonApprovalsPage';
import EliminationManagementPage from '@/pages/admin/EliminationManagementPage';
import { BackfillPicksPage } from '@/pages/admin/BackfillPicksPage';
import { PickRulesManagementPage } from '@/pages/admin/PickRulesManagementPage';
import { ScheduleManagementPage } from '@/pages/admin/ScheduleManagementPage';
import { UsersManagementPage } from '@/pages/admin/UsersManagementPage';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Toaster } from '@/components/ui/toaster';
import { useSeasonApproval } from '@/hooks/useSeasonApproval';
import { ErrorBoundary } from '@/components/ErrorBoundary';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function ApprovalCheckRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { needsApproval, isLoading, isApiDown } = useSeasonApproval();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admins are never subject to season approval checks
  if (user?.isAdmin) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If API is down, let the child component handle it (don't redirect)
  if (isApiDown) {
    return <>{children}</>;
  }

  if (needsApproval) {
    console.log('ApprovalCheckRoute: User needs approval, redirecting to /pending-approval');
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isAdmin) {
    console.log('User is not admin, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      {/* Both are public: someone who has forgotten their password cannot be signed in, and the
          reset link arrives by email in whatever browser they happen to open it in. A signed-in
          user is bounced to the dashboard, the same as /login. */}
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />}
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute>
            <PendingApprovalPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/league"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <div className="container mx-auto p-6">
                <LeagueStandings />
              </div>
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      <Route
        path="/gameweek"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <GameweekPage />
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      {/* Separate route rather than an optional segment, which React Router does not support.
          The bare path stays the most recent gameweek so it does not go stale in a bookmark. */}
      <Route
        path="/gameweek/:gameweekNumber"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <GameweekPage />
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      <Route
        path="/danger-zone"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <DangerZonePage />
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      <Route
        path="/eliminations"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <EliminationsPage />
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      {/* Bare /users is the nav destination and shows the signed-in player. Kept as its own
          route rather than a redirect so the header link is a fixed string that does not have to
          wait for the user to load. */}
      <Route
        path="/users"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <UserProfilePage />
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      <Route
        path="/users/:userId"
        element={
          <ApprovalCheckRoute>
            <Layout>
              <UserProfilePage />
            </Layout>
          </ApprovalCheckRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Layout>
              <AdminLayout>
                <SeasonManagementPage />
              </AdminLayout>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/approvals"
        element={
          <AdminRoute>
            <Layout>
              <AdminLayout>
                <SeasonApprovalsPage />
              </AdminLayout>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <Layout>
              <AdminLayout>
                <UsersManagementPage />
              </AdminLayout>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/eliminations"
        element={
          <AdminRoute>
            <Layout>
              <AdminLayout>
                <EliminationManagementPage />
              </AdminLayout>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/backfill"
        element={
          <AdminRoute>
            <Layout>
              <AdminLayout>
                <BackfillPicksPage />
              </AdminLayout>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/pick-rules"
        element={
          <AdminRoute>
            <Layout>
              <AdminLayout>
                <PickRulesManagementPage />
              </AdminLayout>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/schedule"
        element={
          <AdminRoute>
            <Layout>
              <AdminLayout>
                <ScheduleManagementPage />
              </AdminLayout>
            </Layout>
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    throw new Error('Google Client ID is not configured');
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <GoogleOAuthProvider clientId={googleClientId}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthProvider>
                <SignalRProvider>
                  <AppRoutes />
                  <Toaster />
                </SignalRProvider>
              </AuthProvider>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </GoogleOAuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
